import type { AnimationEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetOverlay,
  SheetTitle,
} from '@parrot-software/design-system';
import { PencilLine } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { logger } from '@parrot-software/observability';
import type { CsfDataViewDto, InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { UnsavedChangesDialog } from '@subscriptions/presentation/components/base';
import { useFiscalRegimes, useUnsavedChangesGuard } from '@subscriptions/presentation/hooks';
import { mapCsfDataToFormValues, resolveInvoiceErrorMessages } from '@subscriptions/presentation/utils';
import { CsfUploadSection } from './CsfUploadSection';
import { InvoiceDataForm } from './InvoiceDataForm';
import type { InvoiceDataFormMode } from './InvoiceDataForm';
import { getInvoicePersonType } from './InvoiceDataForm.schema';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

const FIELDS_CLASS_NAME = 'overflow-y-auto scroll-smooth scroll-pb-5 px-4 pb-5';
const FOOTER_CLASS_NAME = 'shrink-0 p-4';

interface InvoiceDataFormSheetProps {
  blockUuid?: string;
  errorMessage?: string | null;
  initialValues?: InvoiceDataViewDto;
  mode?: InvoiceDataFormMode;
  onlyFailed?: boolean;
  open: boolean;
  paymentUuid?: string | null;
  onCancel: () => void;
  onCloseComplete?: () => void;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (invoiceData: InvoiceDataViewDto) => Promise<void> | void;
}

/**
 * InvoiceDataFormSheet wraps InvoiceDataForm inside a side Sheet (drawer).
 *
 * Title and description are per mode: "Agregar dato fiscal" (create), "Editar dato
 * fiscal" (edit) and "Editar dato fiscal y reintentar" (retry, which also gets its
 * own description). Edit and retry share the same fields and update mutation.
 * `errorMessage` and `paymentUuid` are the retry link's payload: the first is shown in the
 * header, the second is handed to the form so the retry request targets the failed payment.
 * `onCloseComplete` fires after the close animation finishes — use it to defer state
 * resets (mode, initial values) so the panel doesn't flicker during the exit transition.
 *
 * In create mode, the sheet shows a single view with the upload section at the top,
 * a "fill manually" option below, and the form appears when either a document is
 * uploaded or the user clicks to fill manually.
 *
 * Every close path (cancel button, X, overlay click, ESC) is routed through an
 * unsaved-changes guard: when the view holds unsaved input (a dirty form, a selected PDF)
 * a confirmation dialog is shown before discarding.
 *
 * @param {InvoiceDataFormSheetProps} props - Component properties
 * @returns {React.ReactElement} Sheet containing the invoice data creation flow
 */
export const InvoiceDataFormSheet = ({
  blockUuid,
  errorMessage,
  initialValues,
  mode = 'create',
  onlyFailed,
  open,
  paymentUuid,
  onCancel,
  onCloseComplete,
  onOpenChange,
  onSuccess,
}: InvoiceDataFormSheetProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');
  const [csfData, setCsfData] = useState<CsfDataViewDto | undefined>(undefined);
  const [showManualForm, setShowManualForm] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { data: fiscalRegimes } = useFiscalRegimes();

  // Mapped on render, not when the document is read: the regime arrives as a description and
  // only the catalog turns it into the code the select stores, so a catalog that resolves late
  // still lands on the field.
  const prefillValues = useMemo(() => (csfData ? mapCsfDataToFormValues(csfData, fiscalRegimes) : undefined), [
    csfData,
    fiscalRegimes,
  ]);

  // A regime the catalog cannot match leaves the select empty by design, so the printed wording is
  // the only way to tell the match apart from a document that never carried a regime. Waits for the
  // catalog: until it loads there is nothing to match against.
  useEffect(() => {
    if (!csfData || !fiscalRegimes || !prefillValues) return;
    if (prefillValues.taxRegimeSatCode || !csfData.taxRegimeDescription.trim()) return;

    invoiceLog.warn('resolveCsfTaxRegime', {
      taxRegimeDescription: csfData.taxRegimeDescription,
      personType: getInvoicePersonType(prefillValues.rfc) ?? 'unknown',
    });
  }, [csfData, fiscalRegimes, prefillValues]);

  const isCreate = mode === 'create';
  const isRetry = mode === 'retry';

  const copyByMode: Record<InvoiceDataFormMode, { description: string; title: string }> = {
    create: { description: t('invoiceDataForm.description'), title: t('invoiceDataForm.title') },
    edit: { description: t('invoiceDataForm.description'), title: t('invoiceDataForm.editTitle') },
    retry: { description: t('invoiceDataForm.retryDescription'), title: t('invoiceDataForm.retryTitle') },
  };
  const { title, description } = copyByMode[mode];

  // The backend reports the failure in SAT/PAC vocabulary, and packs several of them into one
  // string; this turns it into the list of things the user can actually go fix in the form. The
  // record's own RFC is handed in because the message arrives with it redacted (Charter §5.1).
  const errorMessages = resolveInvoiceErrorMessages(errorMessage, t, initialValues?.rfc);

  // In create mode, show form when user uploaded a doc OR clicked manual
  const showForm = isCreate ? Boolean(csfData) || showManualForm : true;
  // Show manual button only in create mode when form is not yet visible and not extracting
  const showManualButton = isCreate && !showForm && !isExtracting;

  const resetCreateState = useCallback(() => {
    setCsfData(undefined);
    setShowManualForm(false);
    setIsExtracting(false);
    setUploadedFile(null);
  }, []);

  // The extracted fields are a draft, not a saved record: they go into the form and the user
  // confirms them there.
  const handleCsfExtracted = useCallback((extracted: CsfDataViewDto) => {
    invoiceLog.info('selectInvoiceDataMethod', { method: 'upload' });
    setCsfData(extracted);
  }, []);

  const handleSelectManual = useCallback(() => {
    invoiceLog.info('selectInvoiceDataMethod', { method: 'manual' });
    setShowManualForm(true);
  }, []);

  const handleLoadingChange = useCallback((loading: boolean) => {
    setIsExtracting(loading);
  }, []);

  /** Updates the uploaded file state (controlled mode for CsfUploadSection). */
  const handleFileChange = useCallback((file: File | null) => {
    setUploadedFile(file);
    // When file is removed, reset to initial state (show dropzone only)
    if (!file) {
      setCsfData(undefined);
      setShowManualForm(false);
    }
  }, []);

  const { cancelClose, confirmClose, isConfirmOpen, requestClose, setIsDirty } = useUnsavedChangesGuard({
    onClose: onCancel,
  });

  // Track dirty state from both upload section and form
  const uploadDirtyRef = useRef(false);
  const formDirtyRef = useRef(false);

  const updateDirtyState = useCallback(() => {
    setIsDirty(uploadDirtyRef.current || formDirtyRef.current);
  }, [setIsDirty]);

  const handleUploadDirtyChange = useCallback(
    (isDirty: boolean) => {
      uploadDirtyRef.current = isDirty;
      updateDirtyState();
    },
    [updateDirtyState],
  );

  const handleFormDirtyChange = useCallback(
    (isDirty: boolean) => {
      formDirtyRef.current = isDirty;
      updateDirtyState();
    },
    [updateDirtyState],
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(nextOpen);
      return;
    }
    requestClose();
  };

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.currentTarget.dataset.state === 'closed') {
      // Deferred like the parent's own resets, so the view does not switch under the
      // exit transition.
      resetCreateState();
      uploadDirtyRef.current = false;
      formDirtyRef.current = false;
      onCloseComplete?.();
    }
  };

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetOverlay className="z-1300 bg-black/50" />
      <SheetContent
        className="w-full max-w-full sm:w-[385px] sm:max-w-[385px] flex flex-col gap-0 p-0 font-sans z-1350"
        onAnimationEnd={handleAnimationEnd}
      >
        <SheetHeader className="shrink-0 p-4 text-left">
          <SheetTitle className="text-lg">{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>

          {isRetry && errorMessages.length > 0 && (
            <div className="mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive">
              <p className="text-sm font-bold">{t('invoiceDataForm.retryErrorTitle')}</p>
              {errorMessages.length === 1 ? (
                <p className="text-sm">{errorMessages[0]}</p>
              ) : (
                <ul className="list-disc pl-4 text-sm">
                  {errorMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </SheetHeader>

        {isCreate ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {!showForm && (
              <>
                <CsfUploadSection
                  className="px-4 pt-2"
                  file={uploadedFile}
                  onDirtyChange={handleUploadDirtyChange}
                  onExtracted={handleCsfExtracted}
                  onFileChange={handleFileChange}
                  onLoadingChange={handleLoadingChange}
                />

                {showManualButton && (
                  <div className="flex px-4 py-3">
                    <Button
                      className="h-auto p-0 text-xs font-normal text-muted-foreground hover:text-primary"
                      onClick={handleSelectManual}
                      type="button"
                      variant="link"
                    >
                      <PencilLine className="mr-1 h-3 w-3" />
                      {t('invoiceDataForm.methodStep.manualTitle')}
                    </Button>
                  </div>
                )}

                <div className={cn('mt-auto', FOOTER_CLASS_NAME)}>
                  <Button className="w-full" onClick={requestClose} type="button" variant="outline">
                    {t('invoiceDataForm.cancel')}
                  </Button>
                </div>
              </>
            )}

            {showForm && (
              <InvoiceDataForm
                blockUuid={blockUuid}
                fieldsClassName={cn(FIELDS_CLASS_NAME, 'pt-4')}
                footerClassName={FOOTER_CLASS_NAME}
                headerContent={
                  <CsfUploadSection
                    className="-mx-4 px-4 pb-2"
                    file={uploadedFile}
                    onDirtyChange={handleUploadDirtyChange}
                    onExtracted={handleCsfExtracted}
                    onFileChange={handleFileChange}
                    onLoadingChange={handleLoadingChange}
                  />
                }
                mode={mode}
                onCancel={requestClose}
                onDirtyChange={handleFormDirtyChange}
                onSuccess={onSuccess}
                prefillValues={prefillValues}
                shouldReset={!open}
              />
            )}
          </div>
        ) : (
          <InvoiceDataForm
            blockUuid={blockUuid}
            fieldsClassName={FIELDS_CLASS_NAME}
            footerClassName={FOOTER_CLASS_NAME}
            initialValues={initialValues}
            mode={mode}
            onCancel={requestClose}
            onDirtyChange={setIsDirty}
            onSuccess={onSuccess}
            onlyFailed={onlyFailed}
            paymentUuid={paymentUuid}
            shouldReset={!open}
          />
        )}

        <UnsavedChangesDialog onCancel={cancelClose} onConfirm={confirmClose} open={isConfirmOpen} />
      </SheetContent>
    </Sheet>
  );
};
